import { useEffect, useRef } from 'react';
import { useModal } from '@ebay/nice-modal-react';
import { useQueryClient } from '@tanstack/react-query';
import { PlusIcon } from '@zep-us/zus-icons';
import { useTranslation } from 'next-i18next';
import { useDialog } from '@zep/dialog';
import { Button, Spacing } from '@zep/ui';
import { PlanKeys } from '../../../../../consts';
import { CreditCardIcon, RotateCircleIcon } from '../../../../icons';
import { paymentFailureFromError } from '../../../payment-tracking/paymentFailure';
import { createPaymentFailureDialog } from '../../../payment-tracking/PaymentFailureContent';
import { readPaymentError } from '../../../subscription/paymentError';
import { RegisterPaymentCardModal } from '../../../subscription/paymentForm/PaymentCardModal/PaymentCardModal';
import { usePlanPaymentFormContext, usePlanPaymentInfoContext } from '../../PlanPaymentSection/Providers';

// 서버가 내려주는 카드 등록 실패 에러 모양(status 코드 + data.message).

export const CardPaymentRegisterSection = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    spaceHashId,
    planPaymentInfo
  } = usePlanPaymentInfoContext();
  const {
    watch,
    setValue,
    formState
  } = usePlanPaymentFormContext();
  const isCardRegistered = watch('isCardRegistered');
  const existingCard = planPaymentInfo?.billingInfo;
  const hasRegisteredCard = useRef<boolean | undefined>(existingCard === undefined ? undefined : existingCard !== null);
  if (hasRegisteredCard.current === undefined && existingCard !== undefined) hasRegisteredCard.current = existingCard !== null;
  // 구독 실패로 방금 등록한 카드가 삭제되면 카드 정보가 다시 조회된다. 등록/변경 판단을 서버 상태에 맞춘다.
  useEffect(() => {
    if (existingCard !== undefined) hasRegisteredCard.current = existingCard !== null;
  }, [existingCard]);
  const queryClient = useQueryClient();
  const registerCardModal = useModal(RegisterPaymentCardModal);
  const dialog = useDialog();
  const showEditPaymentCardModal = async () => {
    const paymentFlow = hasRegisteredCard.current === undefined ? undefined : hasRegisteredCard.current ? 'card_change' as const : 'card_registration' as const;
    await registerCardModal.show({
      spaceHashId,
      paymentFlow,
      onSuccess() {
        hasRegisteredCard.current = true;
        setValue('isCardRegistered', true);
        queryClient.invalidateQueries(PlanKeys.spaceDetail(spaceHashId)).catch(console.error);
        registerCardModal.hide().catch(console.error);
      },
      onError: async (error: unknown) => {
        const {
          status,
          message
        } = readPaymentError(error);
        const failureDialog = paymentFlow ? createPaymentFailureDialog(dialog, {
          payment_flow: paymentFlow,
          pg_provider: 'toss',
          space_id: planPaymentInfo?.spaceId,
          ...paymentFailureFromError(error)
        }, error) : dialog;
        setValue('isCardRegistered', false);
        if (status === -157) {
          await failureDialog.alert({
            title: t('subscription:payment.alertFailCardRegister.title'),
            content: t('subscription:SpaceDetail.changeCardModal.reason', {
              message: message ?? 'Processing Failure'
            })
          });
          return;
        }

        // FAIL_TO_CREATE_BILLING_KEY
        // FIXME: (주의) 서버의 error 객체의 depth가 error.data.message 일수 있고 error.message 일수 있음;;;
        if (status === -155) {
          await failureDialog.alert({
            title: t('subscription:payment.alertRegisterFail.title', '카드 등록 실패'),
            content: t('subscription:payment.alertRegisterFail.descriptionWithReason', {
              defaultValue: '사유: {{failReason}}',
              failReason: message || '카드 입력 정보를 다시 확인해주세요.'
            })
          });
          return;
        }
        await failureDialog.alert({
          title: 'UNKNOWN_ERROR',
          content: 'UNKNOWN_ERROR'
        });
      }
    });
  };
  return <>
      {isCardRegistered ? <div className="flex h-[70px] w-full items-center justify-between rounded-[10px] border border-gray-300 bg-white pl-[26px] pr-[24px]">
          <div className="flex items-center gap-2xs">
            <CreditCardIcon width={18} height={18} />
            <span className="text-body-1-sb font-semibold text-gray-900">
              {planPaymentInfo?.billingInfo?.cardName ?? '우리우리카드'}
            </span>
          </div>
          <Button size="xs" variant="sub" leftSlot={<RotateCircleIcon width={14} height={14} />} onClick={showEditPaymentCardModal}>
            {t('subscription:payment.cardChange.buttonChange', '결제수단 변경')}
          </Button>
        </div> : <>
          <div className="flex h-[70px] w-full items-center justify-between rounded-[10px] border border-gray-300 bg-gray-50 pl-[26px] pr-[24px]">
            <div className="flex items-center gap-2xs">
              <CreditCardIcon width={18} height={18} />
              <span className="text-body-1-sb font-semibold text-[#27262E8A]">
                {t('subscription:payment.paymentMethod.cardRegister', '결제 카드를 등록해 주세요')}
              </span>
            </div>
            <Button size="xs" leftSlot={<PlusIcon width={14} height={14} />} onClick={showEditPaymentCardModal}>
              {t('subscription:payment.paymentMethod.cardRegisterButton', '카드 등록')}
            </Button>
          </div>
          {formState.errors.isCardRegistered && <div className="flex w-full flex-col justify-start">
              <Spacing size={8} />
              <span className="text-body-2-sb font-semibold text-red">
                {t('subscription:payment.paymentMethod.cardRegister', '결제 카드를 등록해 주세요')}
              </span>
            </div>}
        </>}
    </>;
};